import {ChangeEvent} from 'react';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import difference from 'lodash/difference';
import _intersection from 'lodash/intersection';
import {cityIn} from 'lvovich';
import {OptionSimple as LegoSelectOption} from '@yandex-lego/components/Select';
import {objectEntries, objectKeys} from 'ts-extras';
import {DIRECT_GEO_VALUES} from 'assets/direct/geo_values';

import {
    AudienceCategoryId,
    AudienceCategoryInfo,
    AudienceCategoryPossibleValues,
    AudienceCategoryValue,
    AudienceRequest,
    AudienceSegmentInfo,
    AudienceType,
    BoxType,
    Population,
    POPULATION_VALUES,
    Features
} from 'client/common/types';
import {
    ExtendedAudienceCategoryId,
    GeoValue,
    COUNTRIES,
    COUNTRIES_REGION_FALLBACKS,
    SUPPORTED_COUNTRIES,
    SupportedCountries,
    AudienceSettingsType
} from 'client/common/types/audience';
import {AudienceTargeting, TargetingOptions} from 'client/common/utils/audience';
import {formatAgeIntervals} from 'client/common/utils/format';
import {Option as InlineSelectOption} from 'client/common/components/inline-select/types';
import {getIsCreativeBox} from 'client/common/utils/box';

import I18N from 'assets/i18n/brandlift-project';

import {CREATIVE_ALLOWED_GEO_VALUES} from './consts';
import {UserRoles} from 'client/common/hooks/user';

const i18n = I18N.common.audienceSettings;
const geoTranslations = I18N.common.geo;

export function getDescription(request: AudienceRequest) {
    return request.description;
}

export function getAudienceTypeLabel(type: AudienceSettingsType, boxType?: BoxType) {
    const isCreativeBox = getIsCreativeBox(boxType);

    const keyset = isCreativeBox ? i18n.creativeTypeSelect : i18n.typeSelect;

    const AudienceLabels: Record<AudienceSettingsType, string> = {
        [AudienceType.RespParams]: keyset.categories,
        // TODO не имеет смысла для доп таргетингов, надо переделать или выпилить
        [AudienceType.RespParamsOmi]: keyset.categories,
        [AudienceType.MediaTargeting]: keyset.interests,
        [AudienceType.YaAudience]: keyset.segment,
        [AudienceType.Manual]: keyset.manual,
        [AudienceType.Quotas]: keyset.quotas,
        GEO: keyset.geo
    };

    return AudienceLabels[type];
}

export function getAudienceTypeOptions(params: {
    boxType: BoxType | undefined;
    request: AudienceRequest;
    segments: AudienceSegmentInfo[];
    userRoles: UserRoles;
    features: Features;
    selectedCountry: string | undefined;
}): LegoSelectOption[] {
    const types: AudienceSettingsType[] = AudienceTargeting.getAvailableAudienceTypes(params);

    return types.map(type => {
        return {
            value: type,
            content: getAudienceTypeLabel(type, params.boxType)
        };
    });
}

export type TargetingOptionsType = Record<AudienceCategoryId, AudienceCategoryPossibleValues> &
    Record<ExtendedAudienceCategoryId, AudienceCategoryPossibleValues>;

type TargetingValues = Record<AudienceCategoryId, string[]>;

export function getTargetingLabels(options: TargetingOptionsType, selectedValues: TargetingValues) {
    return {
        age: getAgeLabel(options.age, selectedValues.age),
        gender: getGenderLabel(options.gender, selectedValues.gender),
        geo: getGeoLabel(options.geo, selectedValues.geo)
    };
}

function getAgeLabel(options: InlineSelectOption[], selected: AudienceCategoryValue[]): string {
    if (selected.length === options.length) {
        return i18n.categories.inAllAges;
    }

    const intervals = map(
        options.filter(({value}) => selected.includes(value)),
        'label'
    );

    return formatAgeIntervals(intervals);
}

function getGenderLabel(options: InlineSelectOption[], selected: AudienceCategoryValue[]): string {
    const text = options
        .filter(({value}) => selected.includes(value))
        .map(({label}) => label.toLowerCase())
        .join(` ${i18n.categories.and} `);

    return capitalize(text);
}

const COUNTRY_REGIONS: {[key in SupportedCountries]: {}} = {
    [GeoValue.Russia]: {
        [DIRECT_GEO_VALUES.CITY.SAINT_PETERSBURG]: geoTranslations['2'],
        [DIRECT_GEO_VALUES.CITY.MOSCOW]: geoTranslations['213'],
        [DIRECT_GEO_VALUES.CITY.VOLGOGRAD]: geoTranslations['38'],
        [DIRECT_GEO_VALUES.CITY.VORONEZH]: geoTranslations['193'],
        [DIRECT_GEO_VALUES.CITY.YEKATERINBURG]: geoTranslations['54'],
        [DIRECT_GEO_VALUES.CITY.KAZAN]: geoTranslations['43'],
        [DIRECT_GEO_VALUES.CITY.KRASNODAR]: geoTranslations['35'],
        [DIRECT_GEO_VALUES.CITY.KRASNOYARSK]: geoTranslations['62'],
        [DIRECT_GEO_VALUES.CITY.NIZHNY_NOVGOROD]: geoTranslations['47'],
        [DIRECT_GEO_VALUES.CITY.NOVOSIBIRSK]: geoTranslations['65'],
        [DIRECT_GEO_VALUES.CITY.OMSK]: geoTranslations['66'],
        [DIRECT_GEO_VALUES.CITY.PERM]: geoTranslations['50'],
        [DIRECT_GEO_VALUES.CITY.ROSTOV_NA_DONU]: geoTranslations['39'],
        [DIRECT_GEO_VALUES.CITY.SAMARA]: geoTranslations['51'],
        [DIRECT_GEO_VALUES.CITY.UFA]: geoTranslations['172'],
        [DIRECT_GEO_VALUES.CITY.CHELYABINSK]: geoTranslations['56']
    },
    [GeoValue.Kazakhstan]: {
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.AKMOLINSK_DISTRICT]: geoTranslations['29403'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.ALMA_ATA_DISTRICT]: geoTranslations['29406'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.ATYRAU_DISTRICT]: geoTranslations['29407'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.EAST_KAZAKHSTAN_DISTRICT]: geoTranslations['29408'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.ZHAMBYLSK_DISTRICT]: geoTranslations['29409'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.KARAGANDA_DISTRICT]: geoTranslations['29411'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.KOSTANAI_DISTRICT]: geoTranslations['29412'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.KYZYLORDA_DISTRICT]: geoTranslations['29413'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.MANGYSTAU_DISTRICT]: geoTranslations['29414'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.PAVLODAR_DISTRICT]: geoTranslations['29415'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.NORTH_KAZAKHSTAN_DISTRICT]: geoTranslations['29416'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.TURKESTAN_DISTRICT]: geoTranslations['29417']
    },
    [GeoValue.Belarus]: {
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.GOMEL_DISTRICT]: geoTranslations['29631'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.GRODNO_DISTRICT]: geoTranslations['29634'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.MINSK_DISTRICT]: geoTranslations['29630'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.MOGILEV_DISTRICT]: geoTranslations['29629']
    },
    [GeoValue.Uzbekistan]: {
        [DIRECT_GEO_VALUES.CITY.TASHKENT]: geoTranslations['10335'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.TASHKENT_PROVINCE]: geoTranslations['105813'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.ANDIJAN_PROVINCE]: geoTranslations['105804'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.BUKHARA_PROVINCE]: geoTranslations['105805'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.JIZZAKH_PROVINCE]: geoTranslations['105806'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.QASHQADARYO_PROVINCE]: geoTranslations['105807'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.NAMANGAN_PROVINCE]: geoTranslations['105809'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.REPUBLIC_OF_KARAKALPAKSTAN]:
            geoTranslations['105816'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.SURXONDARYO_PROVINCE]: geoTranslations['105811'],
        [DIRECT_GEO_VALUES.ADMINISTRATIVE_AREA.SIRDARYO_PROVINCE]: geoTranslations['105812']
    }
} as const;

export function filterSameCountryRegions(selected: AudienceCategoryValue[]) {
    let regions = selected.slice();

    regions = typeof regions === 'string' ? [regions] : regions;

    if (selected.includes(GeoValue.Russia)) {
        regions = regions.filter(
            item => !Object.keys(COUNTRY_REGIONS[GeoValue.Russia]).includes(String(item))
        );
    } else if (selected.includes(GeoValue.Kazakhstan)) {
        regions = regions.filter(
            item => !Object.keys(COUNTRY_REGIONS[GeoValue.Kazakhstan]).includes(String(item))
        );
    } else if (selected.includes(GeoValue.Belarus)) {
        regions = regions.filter(
            item => !Object.keys(COUNTRY_REGIONS[GeoValue.Belarus]).includes(String(item))
        );
    } else if (selected.includes(GeoValue.Uzbekistan)) {
        regions = regions.filter(
            item => !Object.keys(COUNTRY_REGIONS[GeoValue.Uzbekistan]).includes(String(item))
        );
    }

    return regions;
}

export function getGeoLabel(
    options: InlineSelectOption[],
    selected: AudienceCategoryValue[]
): string {
    const regions = filterSameCountryRegions(selected);

    const selectedOptions = options.filter(({value}) => regions.includes(value));

    return selectedOptions
        .map(({label, value}) => {
            // Библиотека не может определить пол Беларуси
            if (value === GeoValue.Belarus) {
                return cityIn(label, 'female');
            }

            return cityIn(label);
        })
        .join(', ');
}

// инфинитив
export function getStandardGeoLabels(
    options: InlineSelectOption[],
    selected: AudienceCategoryValue[]
): string {
    const regions = filterSameCountryRegions(selected);

    const selectedOptions = options.filter(({value}) => regions.includes(value));

    return selectedOptions.map(({label}) => label).join(', ');
}

/**
 * Если выбрали страну, выделяем все регионы (из селекта?) страны
 */
export function selectAllOptionsWhenCountrySelected(
    options: InlineSelectOption[],
    selected: AudienceCategoryValue[]
): AudienceCategoryValue[] {
    if (_intersection(COUNTRIES, selected).length) {
        return map(options, 'value');
    }

    return selected;
}

/** Одновременно может быть выбрана либо страна, либо ее регион */
export function deselectSameCountryRegions(
    previousGeoList: string[],
    currentGeoList: string[]
): AudienceCategoryValue[] {
    if (!currentGeoList.length) {
        return previousGeoList;
    }

    for (const [country, fallbackRegion] of objectEntries(COUNTRIES_REGION_FALLBACKS)) {
        // выбрали страну целиком, отбрасываем регионы страны
        if (currentGeoList.includes(country) && !previousGeoList.includes(country)) {
            return [country];
        }

        if (previousGeoList.includes(country) && !currentGeoList.includes(country)) {
            // была выбрана страна, сняли выделение со страны, выбираем крупный регион страны
            return [fallbackRegion];
        }

        // была выбрана страна, выбрали регионы
        if (
            previousGeoList.includes(country) &&
            _intersection(currentGeoList, objectKeys(COUNTRY_REGIONS[country]))
        ) {
            // оставляем регионы, отбрасываем страну
            return currentGeoList.filter(geo => geo !== country);
        }
    }

    return currentGeoList;
}

export function filterGeoOptions(options: InlineSelectOption[], boxType: BoxType | undefined) {
    const isCreativeBox = getIsCreativeBox(boxType);

    if (isCreativeBox) {
        return options.filter(({value}) => CREATIVE_ALLOWED_GEO_VALUES.includes(value as GeoValue));
    }

    return options;
}

export function filterCountryRegionsChangeOnCountrySelect(
    previous: AudienceCategoryValue[],
    onChange: (value: AudienceCategoryValue[]) => void
) {
    // eslint-disable-next-line max-statements
    return (next: AudienceCategoryValue[]) => {
        // Если выбрали страну, передаем только страну без регионов в onChange, отбрасываем регионы страны.
        for (const country of SUPPORTED_COUNTRIES) {
            if (!previous.includes(country) && next.includes(country)) {
                onChange([country]);

                return true;
            }
        }

        /*
            Если убрали выделение со страны, выбираем самый крупный регион страны,
            чтобы оставался выбран хотя бы один регион текущей страны
            (иначе бэк сфоллбэчится на всю Россию)
        */
        for (const [country, fallbackRegion] of Object.entries(COUNTRIES_REGION_FALLBACKS)) {
            if (previous.includes(country) && !next.includes(country)) {
                onChange([fallbackRegion]);

                return true;
            }
        }

        // @ts-ignore
        return onChange(next.filter(value => !COUNTRIES.includes(value as GeoValue)));
    };
}

export function getPopulationToValue(categories: AudienceCategoryInfo[]) {
    const possibleValues = TargetingOptions.getGeoPossibleValues(categories);

    return POPULATION_VALUES.reduce(
        (acc, population) => {
            acc[population] = possibleValues
                .filter(({properties}) => properties.population === population)
                .map(({value: val}) => val);

            return acc;
        },
        {} as {[key in Population]: string[]}
    );
}

export function getCanUsePopulationSelect(
    value: AudienceCategoryValue[],
    categories: AudienceCategoryInfo[]
) {
    // Если таргетинг на всю Россию - то можно
    if (
        isEqual(value, []) ||
        isEqual(value, [GeoValue.Russia]) ||
        // Тесты не запускаются вне России, так как объем Толоки в других странах очень мал и не достаточен для реализации проекта,
        // с учетом выборки на каждый тест (300 респондентов).
        isEqual(value, [GeoValue.Kazakhstan]) ||
        isEqual(value, [GeoValue.Uzbekistan]) ||
        isEqual(value, [GeoValue.Belarus])
    ) {
        return true;
    }

    let diff = cloneDeep(value);
    const populationToValue = getPopulationToValue(categories);

    // Для каждого значения population проверяем входят ли все значения в value, либо не входит ни одно
    // Если нет - значит селект использовать нельзя
    for (const population of POPULATION_VALUES) {
        const populationValue = populationToValue[population];
        const intersection = _intersection(value, populationValue);

        if (isEmpty(intersection) || isEqual(intersection.sort(), populationValue.sort())) {
            diff = difference(diff, intersection);
            continue;
        } else {
            return false;
        }
    }

    // Проверяем не осталось ли городов без property.population
    return isEmpty(diff);
}

export function getSelectedPopulation(
    value: AudienceCategoryValue[],
    categories: AudienceCategoryInfo[]
) {
    const populationToValue = getPopulationToValue(categories);

    const selected: Array<'allRussia' | '1M+' | '500k+'> = POPULATION_VALUES.filter(
        population => difference(populationToValue[population], value).length === 0
    );

    if (value.includes(GeoValue.Russia)) {
        selected.push('allRussia');
    }
    // Тесты не запускаются вне России, так как объем Толоки в других странах очень мал и не достаточен для реализации проекта,
    // с учетом выборки на каждый тест (300 респондентов).
    if (value.includes(GeoValue.Kazakhstan)) {
        selected.push('allRussia');
    }
    if (value.includes(GeoValue.Belarus)) {
        selected.push('allRussia');
    }
    if (value.includes(GeoValue.Uzbekistan)) {
        selected.push('allRussia');
    }

    return selected;
}

export const convertToLegoOptions = (opts: InlineSelectOption[]) =>
    opts.map(({value, label}) => ({value, content: label}));

type HandleSelectCallback = (val: string[]) => void;

export const handleSelectChange =
    (cb: HandleSelectCallback) => (event: ChangeEvent<HTMLSelectElement>) => {
        const {value} = event.target;

        // чтобы всегда оставался выбранный вариант
        if (value.length === 0) {
            return;
        }

        cb(value as any);
    };
